import type { Item, Status, List } from "~/types/api";
import type { StatusGroup, TabWindow, TabWindowState } from "../../task.types";

export const groupItemsByStatus = (items: Item[], statuses: Status[]): StatusGroup[] => {
  const uniqueStatusCodes = Array.from(new Set(items.map((i) => i.statusCode)));

  const maxWorkflowByTask = new Map<number, number>();
  for (const item of items) {
    const taskId = item.taskId ?? 0;
    const wId = item.workflowId ?? 0;
    const current = maxWorkflowByTask.get(taskId) ?? 0;
    if (wId > current) maxWorkflowByTask.set(taskId, wId);
  }

  return uniqueStatusCodes.map((code) => {
    const status = statuses.find((s) => s.code === code);
    const statusName = status?.name || `Status ${code}`;
    const itemsInGroup = items.filter((i) => i.statusCode === code);

    const isDeprecated = itemsInGroup.every((i) => {
      const taskId = i.taskId ?? 0;
      const maxWId = maxWorkflowByTask.get(taskId) ?? 0;
      return maxWId > 0 && (i.workflowId ?? 0) < maxWId;
    });

    return {
      id: code,
      name: statusName,
      isDeprecated,
      children: itemsInGroup,
    };
  });
};

export const restoreMinimizedWindow = <T>(
  getWindow: (id: string) => TabWindow<T> | undefined,
  updateWindowState: (id: string, state: TabWindowState) => void,
  windowId: string
): boolean => {
  const existingWindow = getWindow(windowId);
  if (existingWindow) {
    if (existingWindow.state.isMinimized) {
      updateWindowState(windowId, { ...existingWindow.state, isMinimized: false });
    }
    return true;
  }
  return false;
};

export const ALL_TASKS_TITLE = "All Tasks";
export const ALL_BRAND_TASKS_TITLE = " - " + ALL_TASKS_TITLE;

export const URL_PARAM_OPEN_TASK_ID = "openTaskId";
export const URL_PARAM_OPEN_TASKS = "openTasks";
export const URL_PARAM_VIEW_ALL_TASKS = "viewAllTasks";
export const URL_PARAM_VIEW_ALL_BRAND_TASKS = "viewAllBrandTasks";

export const ALL_TASKS_BRAND_PREFIX = "window-all-brand-tasks-";
export const ALL_TASKS_PREFIX = "window-all-tasks";
export const TASKS_PREFIX = "window-task-";

export const getTaskIdFromWindow = (
  windowId: string,
  prefix: string = TASKS_PREFIX
): number | null => {
  if (windowId.startsWith(prefix)) {
    const taskId = Number(windowId.replace(prefix, ""));
    return isNaN(taskId) ? null : taskId;
  }
  return null;
};

export const getTaskIdsFromWindowState = (state: TabWindowState): number[] => {
  if (state.taskIds && state.taskIds.length > 0) return state.taskIds;

  const [id] = state.taskIds ?? [];
  return id !== undefined ? [id] : [];
};

export const getDistinctWorkflowIds = (groups: StatusGroup[]): number[] => {
  const workflowIdSet = new Set<number>();

  for (const group of groups) {
    for (const item of group.children) {
      if (item.workflowId) {
        workflowIdSet.add(item.workflowId);
      }
    }
  }

  return Array.from(workflowIdSet);
};

export const applyListFilters = (
  items: Item[],
  lists: List[],
  appliedListIds: number[]
): Item[] => {
  if (!appliedListIds || appliedListIds.length === 0) return items;

  const allStatusCodes = new Set<number>();
  let hasAnyStatuses = false;

  appliedListIds.forEach((listId) => {
    const list = lists.find((l) => l.id === listId);
    if (list && list.statuses && list.statuses.length > 0) {
      hasAnyStatuses = true;
      list.statuses.forEach((s) => allStatusCodes.add(s.code));
    }
  });

  if (!hasAnyStatuses) return items;

  return items.filter((item) => allStatusCodes.has(item.statusCode));
};
